import { equipmentOptions } from 'functions/equipmentUtils'
import { CircleMinusIcon, CirclePlusIcon } from 'lucide-react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { TrashIcon } from '@heroicons/react/24/outline'

const DISPLAY_EXERCISE_ID = false
const DISPLAY_EXERCISE_COLLECTION_CYCLE_EXERCISE_PROGRAM_LEVEL_SCORE_RANGE = false
const DISPLAY_EXERCISE_COLLECTION_CYCLE_IDS = false
const WORKOUT_SLOT_COMPLETION_THRESHOLD = 0.5

const WorkoutSlotMeetsCompletionThreshold = (workoutSlot) => {
    return (workoutSlot?.workoutSlotCompletionPercentage ?? 0) >= WORKOUT_SLOT_COMPLETION_THRESHOLD
}

export default function ExerciseCollectionCycleExercise({
    exerciseHashMap = {},
    programLevelHashMap = {},
    exerciseCollection = {},
    setExerciseCollection = () => {},
    requireProgramLevelTagging = 0,
    exerciseCollectionCycleIndex = 0,
    exerciseCollectionCycleExerciseIndex = 0,
    exerciseCollectionCycleExercise = {},
    allowEditing = false,
    highlightedExerciseId = null,
    highlightInjuryOverrides = false,
}) {

    const MoveExerciseBackwardInProgression = (index) => {
        if (index === 0) {
            return
        }

        __UpdateExerciseCollectionProgressionOrder(index, index - 1)
    }

    const MoveExerciseForwardInProgression = (index) => {
        if (index === (exerciseCollection?.exerciseCollectionCycles?.length ?? 0) - 1) {
            return
        }

        __UpdateExerciseCollectionProgressionOrder(index, index + 1)
    }

    const UpdateExerciseOccurrencesAssigned = (configIndex, exerciseIndex, occurrencesAssigned) => {
        const clampedOccurrencesAssigned = Math.max(1, occurrencesAssigned)

        setExerciseCollection((prev) => {
            if (prev === null) {
                return prev
            }

            return {
                ...prev,
                exerciseCollectionCycles: prev.exerciseCollectionCycles.map((config, currentConfigIndex) => {
                    if (currentConfigIndex !== configIndex) {
                        return config
                    }

                    return {
                        ...config,
                        exerciseCollectionCycleExercises: config.exerciseCollectionCycleExercises.map((exercise, currentExerciseIndex) => {
                            if (currentExerciseIndex !== exerciseIndex) {
                                return exercise
                            }

                            return {
                                ...exercise,
                                occurrencesAssigned: clampedOccurrencesAssigned
                            }
                        })
                    }
                })
            }
        })
    }

    const RemoveExerciseFromProgression = (configIndex, exerciseIndex) => {
        setExerciseCollection((prev) => {
            if (prev === null) {
                return prev
            }

            return {
                ...prev,
                exerciseCollectionCycles: prev.exerciseCollectionCycles.filter((_, index) => index !== configIndex)
            }
        })
    }

    const __UpdateExerciseCollectionProgressionOrder = (fromIndex, toIndex) => {
        setExerciseCollection(prev => {
            const updatedJSON = [...prev.exerciseCollectionCycles]
            const [moved] = updatedJSON.splice(fromIndex, 1)
            updatedJSON.splice(toIndex, 0, moved)
            return { ...prev, exerciseCollectionCycles: updatedJSON }
        })
    }

    const programLevelId = exerciseCollectionCycleExercise.programLevelId ?? null
    const equipmentFilterPassed = exerciseCollectionCycleExercise.equipmentFilterPassed ?? 1
    const injuryFilterPassed = exerciseCollectionCycleExercise.injuryFilterPassed ?? 1
    // Injury additions only get the highlight styling while highlighting injury overrides. Otherwise they look like any other exercise
    const injuryAddition = highlightInjuryOverrides ? (exerciseCollectionCycleExercise.injuryAddition ?? 0) : 0
    const databaseExercise = exerciseHashMap[exerciseCollectionCycleExercise.exerciseId] ?? null
    const workoutSlots = Array.isArray(exerciseCollectionCycleExercise.workoutSlots) ? exerciseCollectionCycleExercise.workoutSlots : []
    const occurrencesAssigned = exerciseCollectionCycleExercise.occurrencesAssigned ?? 0
    const visibleWorkoutSlots = workoutSlots.filter(WorkoutSlotMeetsCompletionThreshold)
    const fillCount = visibleWorkoutSlots.length
    const fillPercent = occurrencesAssigned <= 0 ? 0 : Math.min(100, Math.max(0, (fillCount / occurrencesAssigned) * 100))
    const label = `${fillCount}/${occurrencesAssigned}`
    const segmentWidthPercent = occurrencesAssigned > 0 ? 100 / Math.max(occurrencesAssigned, visibleWorkoutSlots.length) : 0
    const isHighlighted = highlightedExerciseId != null && exerciseCollectionCycleExercise.exerciseId === highlightedExerciseId

    if (databaseExercise === null) return null

    return (
        <div className='space-y-2'>
            <div className='relative w-24'>
                <div 
                    className={`
                        flex flex-col items-center rounded-xl overflow-hidden border border-gray-400 p-1 text-center space-y-1
                        ${isHighlighted ? 'bg-orange-100' : 'bg-white'}
                    `}
                >
                    <div className="flex h-8 w-full items-center justify-center px-0.5">
                        <p
                            className="w-full text-center text-[10px] leading-tight line-clamp-2"
                            title={databaseExercise.name}
                        >
                            {DISPLAY_EXERCISE_ID && <span className='text-[10px] font-bold'>({exerciseCollectionCycleExercise.exerciseId}) </span>}
                            {DISPLAY_EXERCISE_COLLECTION_CYCLE_IDS && <>({exerciseCollectionCycleExercise.exerciseCollectionCycleId}) </>}
                            {databaseExercise.name}
                        </p>
                    </div>

                    <div className='relative'>
                        <img
                            src={databaseExercise.imageUrl}
                            alt="Exercise Image"
                            className='w-20 h-24 object-cover rounded-md'
                        />

                        {exerciseCollectionCycleExercise.id !== -1 && databaseExercise?.equipment.length > 0 &&
                            <div className="absolute bottom-1 right-1 flex p-1 gap-x-1 bg-white rounded-lg">
                                {databaseExercise.equipment.split(',').map((text, index) => (
                                    <img
                                        key={index}
                                        src={equipmentOptions.find((option) => option.key === text).imageURL}
                                        alt="Equipment Icon"
                                        className="h-4"
                                    />
                                ))}
                            </div>
                        }
                    </div>

                    <div className='w-full flex items-center justify-center gap-x-1'>
                        {allowEditing && (
                            <button
                                className='py-1 rounded-full'
                                onClick={() => UpdateExerciseOccurrencesAssigned(exerciseCollectionCycleIndex, exerciseCollectionCycleExerciseIndex, occurrencesAssigned - 1)}
                            >
                                <CircleMinusIcon className='h-5 w-5 text-gray-400 pointer-events-none' />
                            </button>
                        )}

                        <div className='relative h-4 w-full overflow-hidden rounded-full bg-gray-200'>
                            {visibleWorkoutSlots.length > 0 && (
                                <div className='absolute inset-0 flex'>
                                    {visibleWorkoutSlots.map((workoutSlot, index) => (
                                        <div
                                            key={workoutSlot.workoutSlotId ?? index}
                                            className='h-full bg-orange-400'
                                            style={{ width: `${segmentWidthPercent}%` }}
                                            title={`${workoutSlot.workoutName ?? 'Workout'} · ${Math.round((workoutSlot.workoutSlotCompletionPercentage ?? 0) * 100)}%`}
                                        />
                                    ))}
                                </div>
                            )}

                            <p className='relative z-10 flex h-full items-center justify-center text-[10px] font-bold text-gray-700'>
                                {label}
                            </p>

                            {fillPercent > 0 &&
                                <div
                                    className='absolute inset-y-0 left-0 z-20 overflow-hidden'
                                    style={{ width: `${fillPercent}%` }}
                                >
                                    <p
                                        className='flex h-full items-center justify-center text-[10px] font-bold text-white'
                                        style={{ width: `${10000 / fillPercent}%` }}
                                    >
                                        {label}
                                    </p>
                                </div>
                            }
                        </div>

                        {allowEditing && (
                            <button
                                className='py-1 rounded-full'
                                onClick={() => UpdateExerciseOccurrencesAssigned(exerciseCollectionCycleIndex, exerciseCollectionCycleExerciseIndex, exerciseCollectionCycleExercise.occurrencesAssigned + 1)}
                            >
                                <CirclePlusIcon className='h-5 w-5 text-gray-400 pointer-events-none' />
                            </button>
                        )}
                    </div>

                    {allowEditing && (
                        <>
                            <div className='w-full grid grid-cols-2 border border-gray-400 divide-x divide-gray-400 rounded-full'>
                                <div
                                    className='rounded-l-full w-full py-1 flex items-center justify-center cursor-pointer hover:bg-gray-100'
                                    onClick={() => MoveExerciseBackwardInProgression(exerciseCollectionCycleIndex)}
                                >
                                    <ChevronLeftIcon className='h-4 w-4 text-gray-500 pointer-events-none' />
                                </div>

                                <div
                                    className='rounded-r-full w-full py-1 flex items-center justify-center cursor-pointer hover:bg-gray-100'
                                    onClick={() => MoveExerciseForwardInProgression(exerciseCollectionCycleIndex)}
                                >
                                    <ChevronRightIcon className='h-4 w-4 text-gray-500 pointer-events-none' />
                                </div>
                            </div>

                            <div className='w-full border border-gray-400 rounded-full'>
                                <div
                                    className='rounded-full w-full py-1 flex items-center justify-center cursor-pointer hover:bg-gray-100'
                                    onClick={() => RemoveExerciseFromProgression(exerciseCollectionCycleIndex, exerciseCollectionCycleExerciseIndex)}
                                >
                                    <TrashIcon className='h-4 w-4 text-gray-500 pointer-events-none' />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {equipmentFilterPassed === 0 && (
                    <div className='absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-gray-500/50 pointer-events-none'>
                        <p className='text-center text-[10px] font-bold leading-tight text-white shadow-md bg-gray-500 py-2'>
                            Skipped by equipment filter!!
                        </p>
                    </div>
                )}

                {highlightInjuryOverrides && equipmentFilterPassed !== 0 && injuryFilterPassed === 0 && (
                    <div className='absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-red-500/40 pointer-events-none' data-testid='injury-removed-exercise'>
                        <p className='text-center text-[10px] font-bold leading-tight text-white shadow-md bg-red-500 py-2'>
                            Removed by injury
                        </p>
                    </div>
                )}

                {equipmentFilterPassed !== 0 && injuryAddition === 1 && (
                    <div className='absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-sky-500/40 pointer-events-none' data-testid='injury-addition-exercise'>
                        <p className='text-center text-[10px] font-bold leading-tight text-white shadow-md bg-sky-500 py-2'>
                            Injury addition
                        </p>
                    </div>
                )}
            </div>

            {(
                requireProgramLevelTagging === 1
                && programLevelId !== null
            ) && (
                <div
                    className='rounded-full'
                    style={{
                        backgroundColor: programLevelHashMap[programLevelId]?.colorHex,
                    }}
                >
                    <p className='text-xxs text-center'>{programLevelHashMap[programLevelId]?.name}</p>
                </div>
            )}

            {(
                DISPLAY_EXERCISE_COLLECTION_CYCLE_EXERCISE_PROGRAM_LEVEL_SCORE_RANGE
                && requireProgramLevelTagging === 1
                && programLevelId !== null
            ) && (
                <div
                    className='rounded-full'
                    style={{
                        backgroundColor: programLevelHashMap[programLevelId]?.colorHex,
                    }}
                >
                    <p className='text-xxs text-center'>
                        {Number(exerciseCollectionCycleExercise.programLevelScoreLowerBound ?? 0)}
                        -
                        {Number(exerciseCollectionCycleExercise.programLevelScoreUpperBound ?? 0)}
                    </p>
                </div>
            )}
        </div>
    )
}